import { useEffect, useMemo, useState } from "react";
import { AccountHolderRatePlan } from "../../services/accountService";
import { useSnackbar } from "../../components/Snackbar";
import Switch from "../../components/Switch";
import Panel from "./Panel";
import { displayWalletRates } from "../../utils/walletRates";
import EditControls from "./EditControls";
import Skeleton from "../../components/Skeleton";
import { sortStrings } from "../../utils/sorting";
import useUpdateWalletMutation from "../../hooks/data/mutations/useUpdateWalletMutation";
import { r } from "msw/lib/glossary-c690f512";

export type RatePlan = {
  id: string;
  label: string;
  data?: AccountHolderRatePlan;
};

type Props = {
  accountHolderCode: string;
  accountHolderId: string;
  accountHolderRatePlans: AccountHolderRatePlan[];
  country: string;
  isLoading: boolean;
  isAdminOrCSR: boolean;
};

function Wallets({
  accountHolderCode,
  accountHolderId,
  accountHolderRatePlans,
  country,
  isLoading,
  isAdminOrCSR,
}: Props): JSX.Element {
  const filteredAccountHolderRatePlans: AccountHolderRatePlan[] =
    useMemo(() => {
      const wallets = displayWalletRates([], country);
      return accountHolderRatePlans.filter((accountHolderRatePlan) =>
        wallets.some(
          (wallet) => wallet.id === accountHolderRatePlan.paymentMethodType
        )
      );
    }, [accountHolderRatePlans, country]);

  // Set the initial state of the rate plans coming in. This will be used to reset back to their respected
  // values when cancelling the edit.
  const ratePlansInitialState = useMemo(() => {
    const wallets = displayWalletRates([], country);
    return filteredAccountHolderRatePlans.map((accountHolderRatePlan) => {
      const matchingWallet = wallets.find(
        (wallet) => wallet.id === accountHolderRatePlan.paymentMethodType
      );
      return {
        id: accountHolderRatePlan.paymentMethodType,
        label: matchingWallet?.label || accountHolderRatePlan.paymentMethodType,
        data: matchingWallet?.data || accountHolderRatePlan,
      };
    });
  }, [accountHolderRatePlans, country]);

  const [ratePlans, setRatePlans] = useState<RatePlan[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const { mutate: updateWallet, isLoading: isUpdatingWallet } =
    useUpdateWalletMutation(accountHolderCode);

  const { openSnackbar } = useSnackbar({
    duration: 30000,
    position: "top",
  });

  useEffect(() => {
    if (!isEditing) {
      setRatePlans(ratePlansInitialState);
    }
  }, [ratePlansInitialState, isEditing]);

  // We only want to get the rate plans where the allowedMethod is different. This is
  // important for ensuring we only update wallets that have actually changed.
  const modifiedWallets = useMemo(() => {
    return ratePlans
      .filter((rp) => {
        if (!rp.data) return false;
        const original = accountHolderRatePlans.find(
          (a) => a.paymentMethodType === rp.data!.paymentMethodType
        );
        return !!original && original.allowedMethod !== rp.data.allowedMethod;
      })
      .map((rp) => {
        const original = accountHolderRatePlans.find(
          (a) => a.paymentMethodType === rp.data!.paymentMethodType
        )!;
        // Merge to ensure full AccountHolderRatePlan shape
        return {
          ...original,
          ...rp.data,
          surcharge: false, // Wallets do not have a surcharge so we can safely set this to false
        };
      });
  }, [ratePlans, accountHolderRatePlans]);

  // Toggle the allowed method for a specific rate plan
  const toggleAllowedMethod = (ratePlanId: string) => {
    setRatePlans((previousRatePlans) =>
      previousRatePlans.map((existingRatePlan) => {
        if (existingRatePlan.id !== ratePlanId) {
          return existingRatePlan;
        }

        return {
          ...existingRatePlan, // keep all RatePlan fields
          data: {
            ...existingRatePlan.data, // keep all AccountHolderRatePlan fields
            allowedMethod: !existingRatePlan.data?.allowedMethod,
          },
        } as RatePlan;
      })
    );
  };

  function handleUpdateWallets() {
    updateWallet(
      {
        accountHolderId: accountHolderId,
        ratePlans: modifiedWallets,
      },
      {
        onSuccess() {
          openSnackbar("Wallets were successfully updated", {
            type: "success",
          });
          setIsEditing(false);
        },
        onError() {
          openSnackbar("Failed to update wallets. Please try again.", {
            type: "error",
          });
        },
      }
    );
  }

  return (
    <>
      <Panel className="col-span-7">
        <Panel.Header title="Wallets">
          {isAdminOrCSR && (
            <EditControls
              customId="editWalletsButton"
              isEditing={isEditing}
              isSaving={isUpdatingWallet}
              editButtonProps={{
                disabled: isLoading,
                onClick: () => {
                  setIsEditing(true);
                },
              }}
              saveButtonProps={{
                onClick: () => handleUpdateWallets(),
              }}
              cancelButtonProps={{
                onClick: () => {
                  setIsEditing(false);
                  // Set the ratePlans state back to the initial state
                  setRatePlans(ratePlansInitialState);
                },
              }}
            />
          )}
        </Panel.Header>
        <Panel.Content>
          <div className="flex flex-col gap-y-4">
            {isLoading ? (
              <div className="flex flex-wrap gap-y-6">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex gap-x-6">
                    <Skeleton className="w-20" />
                    <Skeleton className="w-20" />
                    <Skeleton className="w-20" />
                  </div>
                ))}
              </div>
            ) : (
              ratePlans
                .sort((a, b) => sortStrings(a.label, b.label))
                .map((ratePlan, i) => (
                  <div key={ratePlan.id}>
                    <Switch
                      checked={ratePlan.data?.allowedMethod || false}
                      onChange={() => toggleAllowedMethod(ratePlan.id)}
                      disabled={!isEditing}
                      label={ratePlan.label}
                      labelProps={{
                        className: "font-medium w-28 mr-20",
                      }}
                    />
                  </div>
                ))
            )}
          </div>
        </Panel.Content>
      </Panel>
    </>
  );
}

export default Wallets;
